import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";

const PROJECTS = [
  {
    id: "katy",
    code: "Katy",
    title: "Katy Commercial Complex",
    location: "Katy, TX",
    category: "Commercial",
    scope: "Shop drawings · Connection design · Erection plans",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/34v5x7pp_ISO%20VIEW%20%20katy.png",
    description:
      "Multi-block commercial complex with composite floor decks and moment-frame lateral system. Delivered fabrication-ready with anchor bolt setting plans and complete BOMs.",
  },
  {
    id: "alvarado",
    code: "Alvarado",
    title: "Alvarado Mixed-Use Building",
    location: "Alvarado, TX",
    category: "Commercial",
    scope: "Shop drawings · Anchor bolt plans · BIM coordination",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/i3mcz7h3_ISO%20VIEW%20-%20Alvarado.png",
    description:
      "Two-tower stepped commercial structure with orange composite decking and shared podium. Detailed floor-by-floor with staged erection sequencing to meet a tight construction schedule.",
  },
  {
    id: "p-21-3450",
    code: "21.3450",
    title: "Long-Span Roof Structure",
    location: "TX, USA",
    category: "Long-span / Industrial",
    scope: "Truss detailing · Roof bracing · Connection design",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/u1z36fvf_21.3450%20SNAP1.PNG",
    description:
      "Long-span roof with lattice trusses, vertical bracing towers and full connection design. Delivered as clash-free 3D model with issue-ready erection drawings.",
  },
  {
    id: "p-21-3449",
    code: "21.3449",
    title: "Multi-Wing Institutional Building",
    location: "TX, USA",
    category: "Institutional",
    scope: "Full detailing · Erection sequencing · IFC handover",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/xdlazvlh_21.3449%20SNAP.PNG",
    description:
      "Multi-wing institutional building with symmetrical steel-framed wings connected via a central atrium. Complex column grid coordinated across four wings with zero clashes at IFC handover.",
  },
  {
    id: "stair-tower",
    code: "ST-01",
    title: "Egress Stair Tower",
    location: "TX, USA",
    category: "Stairs & Misc Steel",
    scope: "Stair stringer detailing · Rail framing · Anchor plans",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/0s8oopaz_Stair%20tower.png",
    description:
      "Detailed egress stair tower with channel stringers, checkered-plate treads, landings and OSHA-compliant handrails. Every stringer, rail post and anchor plate modeled and drawn shop-ready.",
  },
  {
    id: "stair-tower-20",
    code: "ST-20F",
    title: "20-Flight Stair Tower",
    location: "TX, USA",
    category: "Stairs & Misc Steel",
    scope: "Multi-story stair detailing · Elevation drawings · BOM",
    image_url:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/tyeppgw4_20%20flights%20stair%20tower.png",
    description:
      "20-flight vertical circulation stair tower spanning ten stories. Detailed with matched elevation drawings, precise landing coordinates and complete rail-and-baluster take-offs for the fabricator.",
  },
];

export const CompletedProjects = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const isOpen = openIdx !== null;
  const item = isOpen ? PROJECTS[openIdx] : null;

  const goPrev = () =>
    setOpenIdx((i) => (i === null ? null : (i - 1 + PROJECTS.length) % PROJECTS.length));
  const goNext = () =>
    setOpenIdx((i) => (i === null ? null : (i + 1) % PROJECTS.length));

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-20 md:py-28 border-t border-slate-200/60 bg-slate-50/60"
    >
      <div className="px-6 md:px-10 lg:px-14 xl:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
              <span className="w-8 h-px bg-[#0A2540]" />
              03 · Completed Projects
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.08]">
              Real detailing.
              <br />
              <span className="text-slate-400">Real jobs shipped</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:items-end">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              A selection of steel detailing projects we&apos;ve delivered — from
              multi-tower commercial complexes to long-span industrial roofs.
              Click any project to view the model and scope.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {PROJECTS.map((p, idx) => (
            <motion.button
              key={p.id}
              type="button"
              onClick={() => setOpenIdx(idx)}
              data-testid={`project-card-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.06 }}
              className="group text-left bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-slate-500 hover:shadow-lg hover:shadow-slate-900/20 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A2540] focus-visible:ring-offset-4 focus-visible:ring-offset-slate-50"
            >
              <div className="relative aspect-[4/3] bg-slate-950 overflow-hidden">
                <img
                  src={p.image_url}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-contain transition-transform duration-[800ms] ease-out group-hover:scale-[1.02]"
                />
                <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-slate-950/80 backdrop-blur px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-[0.14em] text-slate-200 border border-white/10">
                  <span className="w-1 h-1 rounded-full bg-white" />
                  {p.category}
                </div>
                <div className="absolute top-2.5 right-2.5 font-mono text-[9px] uppercase tracking-[0.16em] text-white/70 bg-slate-950/80 backdrop-blur px-2 py-0.5 rounded border border-white/10">
                  #{p.code}
                </div>
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
              </div>

              <div className="p-4 bg-white">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold text-base md:text-lg text-[#0A2540] leading-tight truncate">
                      {p.title}
                    </h3>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">
                      {p.location}
                    </div>
                  </div>
                  <div className="shrink-0 w-7 h-7 rounded-full bg-slate-100 group-hover:bg-[#0A2540] flex items-center justify-center transition-colors">
                    <ArrowUpRight
                      className="w-3.5 h-3.5 text-slate-700 group-hover:text-white transition-colors"
                      strokeWidth={2.25}
                    />
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {isOpen && item && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
            onClick={() => setOpenIdx(null)}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl bg-slate-900 rounded-2xl overflow-hidden flex flex-col lg:flex-row max-h-[92vh] shadow-2xl"
            >
              <button
                onClick={() => setOpenIdx(null)}
                data-testid="project-modal-close"
                className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-md transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
              </button>
              <button
                onClick={goPrev}
                data-testid="project-modal-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-md"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
              </button>
              <button
                onClick={goNext}
                data-testid="project-modal-next"
                className="absolute right-4 lg:right-[calc(41.666%+1rem)] top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-md"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
              </button>

              <div className="relative w-full lg:w-[58.333%] bg-slate-950 aspect-[4/3] lg:aspect-auto flex-shrink-0">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-slate-950/80 backdrop-blur border border-white/10 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.16em] text-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {item.category}
                </div>
                <div className="absolute bottom-4 left-4 font-mono text-xs uppercase tracking-[0.22em] text-white/70">
                  #{item.code}
                </div>
              </div>

              <div className="w-full lg:w-[41.666%] p-6 md:p-8 lg:p-10 flex flex-col overflow-y-auto bg-white">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#0A2540] mb-2">
                  Completed project
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-semibold text-[#0A2540] leading-[1.05] tracking-tight">
                  {item.title}
                </h3>

                <p className="mt-4 text-slate-600 text-base leading-relaxed">
                  {item.description}
                </p>

                <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4">
                  <Stat label="Location" value={item.location} />
                  <Stat label="Category" value={item.category} />
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Scope of work
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed">
                    {item.scope}
                  </p>
                </div>

                <div className="mt-auto pt-8">
                  <a
                    href="#contact"
                    onClick={() => setOpenIdx(null)}
                    className="inline-flex w-full items-center justify-center gap-2 bg-[#0A2540] hover:bg-[#0F172A] text-white font-semibold px-6 py-3.5 rounded-full transition-colors"
                  >
                    Discuss a similar project
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-sm font-semibold text-[#0A2540]">{value}</div>
  </div>
);
